import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { VoiceService } from 'app/core';
import firebase from 'firebase/app';
import { Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
    Call,
    CallStatus,
    Channel,
    ChannelType,
    ConferenceOnHoldResponse,
    ProviderEndpoints,
    Ticket,
    VoiceConnectionStatus,
    VoiceDevice,
    VoiceDeviceStatus,
} from '../../models';
import { AuthenticationService } from '../authentication.service';
import { ChannelsService } from '../channels.service';

/**
 * Abstract class that serves as a base for multiple voice providers (e.g. Twilio)
 *
 * @export
 */
@Injectable()
export abstract class ParentVoiceService implements OnDestroy, VoiceService {
    protected device: VoiceDevice;
    protected voiceChannel: Channel;
    protected channelSubscription: Subscription;
    protected deviceStatusSubject = new ReplaySubject<CallStatus>();
    protected connectionErrorSubject = new Subject<{ ticket: Ticket }>();
    public calls: Map<string, Call>;

    abstract providerEndpoints: ProviderEndpoints;

    constructor(
        protected http: HttpClient,
        protected channelService: ChannelsService,
        protected translateService: TranslateService,
        protected authentication: AuthenticationService
    ) {
        this.calls = new Map<string, Call>();
    }

    init(): void {
        this.channelSubscription = this.channelService.channels().subscribe((channels: Channel[]) => {
            if (channels && channels.length > 0) {
                const currentVoiceChannel = channels.find((channel) =>
                    this.channelService.validateChannelTypes([channel.type as ChannelType], ['Phone'])
                );

                if (!!currentVoiceChannel && !this.voiceChannel && this.checkTokenLink()) {
                    this.getNewIdToken().then(async (idToken: string) => {
                        this.device = await this.setupDevice(idToken);
                    });

                    this.voiceChannel = currentVoiceChannel;
                }
            }
        });
    }

    destroy(): void {
        this.shutDownDevice();
    }

    abstract setupDevice(idToken: string): Promise<VoiceDevice>;

    abstract shutDownDevice(): void;

    ngOnDestroy(): void {
        this.channelSubscription.unsubscribe();
    }

    deviceStatus(): Observable<CallStatus> {
        return this.deviceStatusSubject.asObservable();
    }

    connectionError(): Observable<{ ticket: Ticket }> {
        return this.connectionErrorSubject.asObservable();
    }

    abstract hasValidToken(): boolean;

    registerTicket(ticketId: string, conferenceId: string): void {
        if (!this.calls.get(ticketId)) {
            this.calls.set(ticketId, {
                conferenceId,
                connection: null,
            });
        }

        if (!!this.voiceChannel && !this.hasValidToken()) {
            this.getNewIdToken().then((idToken) => {
                this.setupDevice(idToken);
            });
        }
    }

    clearTicket(ticketId: string): void {
        if (this.calls.get(ticketId)) {
            this.calls.delete(ticketId);
        }
    }

    pickUpCall(ticket: Ticket): void {
        this.connect(ticket);
    }

    hasConnection(ticketId: string): boolean {
        const ticket = this.calls.get(ticketId);
        return ticket && ticket.connection && ticket.connection.status() === VoiceConnectionStatus.Open;
    }

    hasCall(ticketId: string): boolean {
        const ticket = this.calls.get(ticketId);
        return !!ticket;
    }

    hasCalls(): boolean {
        return !this.calls || this.calls.size === 0;
    }

    isMuted(ticketId: string): boolean {
        const ticket = this.calls.get(ticketId);
        return ticket && ticket.connection && ticket.connection.isMuted() && this.device.status() === VoiceDeviceStatus.Busy;
    }

    isOnHold(ticketId: string): boolean {
        const ticket = this.calls.get(ticketId);
        return ticket && ticket.onHold && this.device.status() === VoiceDeviceStatus.Busy;
    }

    disconnect(ticketId: string, disconnectCallback?: () => void): void {
        const ticket = this.calls.get(ticketId);
        if (ticket && ticket.connection) {
            ticket.connection.disconnect();

            if (disconnectCallback) {
                disconnectCallback();
            }
        }
    }

    mute(ticketId: string): void {
        const ticket = this.calls.get(ticketId);

        if (ticket && ticket.connection && this.device.status() === VoiceDeviceStatus.Busy) {
            ticket.connection.mute(!ticket.connection.isMuted());
        }
    }

    pause(ticketId: string): void {
        const ticket = this.calls.get(ticketId);
        if (ticket && ticket.connection && this.device.status() === VoiceDeviceStatus.Busy) {
            this.setConferenceParticipantOnHold(ticketId);
        }
    }

    async transfer(ticketId: string, forwardTo: string): Promise<void> {
        const ticket = this.calls.get(ticketId);
        if (ticket && ticket.connection && this.device.status() === VoiceDeviceStatus.Busy) {
            return await this.transferCall(ticketId, forwardTo);
        }

        return null;
    }

    async createCall(originNumber: string, destinationNumber: string, ticketId: string): Promise<any> {
        return await this.newCall(originNumber, destinationNumber, ticketId);
    }

    private checkTokenLink(): boolean {
        return !!this.providerEndpoints.TOKEN_URL;
    }

    private async connect(ticket: Ticket): Promise<void> {
        const call = this.calls.get(ticket.id);

        // Limit the size of the string to send to the connection
        const partialTicket = {
            id: ticket.id,
            preferredLanguages: ticket.preferredLanguages,
            streamId: ticket.streamId,
            channel: ticket.channel,
            chatSessionId: ticket.chatSessionId,
        };

        if (call) {
            call.connection = await this.device.connect({
                conferenceInternalId: call.conferenceId,
                ticket: JSON.stringify(partialTicket),
            });
        }
    }

    private setConferenceParticipantOnHold(ticketId: string): void {
        const ticket = this.calls.get(ticketId);

        this.getNewIdToken().then((idToken: string) => {
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + idToken,
            });

            const options = { headers };

            this.http
                .post(
                    this.providerEndpoints.CONFERENCE_HOLD_URL,
                    {
                        conferenceInternalId: ticket.conferenceId,
                        setOnHold: !ticket.onHold,
                    },
                    options
                )
                .pipe(
                    catchError((error) => {
                        console.log(error);
                        return of({});
                    })
                )
                .subscribe(
                    (response: ConferenceOnHoldResponse) => {
                        if (response) {
                            console.log(`Conference participant on hold status changed to ${response.result}`);
                            ticket.onHold = !!response.result ? !ticket.onHold : ticket.onHold;
                        }
                    },
                    (error) => {
                        console.log(error);
                    }
                );
        });
    }

    private async transferCall(ticketId: string, forwardTo?: string): Promise<any> {
        const ticket = this.calls.get(ticketId);
        const conferenceInternalId = ticket.conferenceId;
        const transferType = forwardTo ? 'forward' : 'transfer';

        const idToken = this.getNewIdToken();

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken,
        });

        const options = { headers };

        return await this.http
            .post(
                this.providerEndpoints.CALL_TRANSFER_URL,
                {
                    conferenceInternalId,
                    transferType,
                    forwardTo,
                },
                options
            )
            .pipe(
                catchError((error) => {
                    console.log(error);
                    return of({});
                })
            )
            .toPromise();
    }

    private async newCall(originNumber: string, destinationNumber: string, ticketId: string): Promise<any> {
        const idToken = this.getNewIdToken();

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken,
        });

        const options = { headers };

        return await this.http
            .post(
                this.providerEndpoints.CREATE_CALL_URL,
                {
                    to: destinationNumber,
                    from: originNumber,
                    ticketId,
                },
                options
            )
            .pipe(
                catchError((error) => {
                    if (error.statusCode === 400) {
                        return of({
                            errorMessage: this.translateService.instant('APP.TICKETS_CHAT.CREATE_CALL_ERROR_400_MESSAGE'),
                            errorMessageTitle: this.translateService.instant('APP.TICKETS_CHAT.CREATE_CALL_ERROR_400_TITLE'),
                        });
                    }
                    return of({
                        errorMessage: this.translateService.instant('APP.TICKETS_CHAT.CREATE_CALL_ERROR_DEFAULT_MESSAGE'),
                        errorMessageTitle: this.translateService.instant('APP.TICKETS_CHAT.CREATE_CALL_ERROR_DEFAULT_TITLE'),
                    });
                })
            )
            .toPromise();
    }

    protected async getNewIdToken(): Promise<string> {
        return await firebase.firestore().app.auth().currentUser.getIdToken();
    }
}
